'use client';

import { useEffect, useRef, useState } from 'react';
import { startTimer, endTimer, logPerformance } from '../utils/performanceLogger';

// Check if debug mode and performance monitoring are enabled from environment variables
const isDebugEnabled = () => {
  try {
    return process.env.NEXT_PUBLIC_DEBUG === 'true' &&
           process.env.NEXT_PUBLIC_ENABLE_PERFORMANCE_MONITORING === 'true';
  } catch (e) {
    return false;
  }
};

interface PerformanceMonitorProps {
  pageName: string;
  children: React.ReactNode;
}

/**
 * Component to monitor page load performance
 * Wrap your page component with this to measure its performance
 */
const PerformanceMonitor: React.FC<PerformanceMonitorProps> = ({ pageName, children }) => {
  const hasLoggedRef = useRef(false);
  const [isDebug, setIsDebug] = useState(false);

  // Initialize isDebug state after mount
  useEffect(() => {
    setIsDebug(isDebugEnabled());
  }, []);

  useEffect(() => {
    if (!isDebug) return;

    try {
      // Only log on initial mount
      if (!hasLoggedRef.current) {
        hasLoggedRef.current = true;

        // Log navigation timing metrics
        if (typeof window !== 'undefined' && window.performance) {
          try {
            const navigation = window.performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;

            if (navigation) {
              // Calculate key metrics
              const dnsTime = navigation.domainLookupEnd - navigation.domainLookupStart;
              const tcpTime = navigation.connectEnd - navigation.connectStart;
              const ttfb = navigation.responseStart - navigation.requestStart;
              const downloadTime = navigation.responseEnd - navigation.responseStart;
              const domInteractive = navigation.domInteractive - navigation.fetchStart;
              const domContentLoaded = navigation.domContentLoadedEventEnd - navigation.fetchStart;
              const domComplete = navigation.domComplete - navigation.fetchStart;
              const loadEvent = navigation.loadEventEnd - navigation.fetchStart;

              // Log the metrics
              console.log(`[PERFORMANCE] Page: ${pageName} - Navigation Timing Metrics:`);
              console.log(`[PERFORMANCE] DNS lookup: ${dnsTime.toFixed(2)}ms`);
              console.log(`[PERFORMANCE] TCP connection: ${tcpTime.toFixed(2)}ms`);
              console.log(`[PERFORMANCE] Time to First Byte: ${ttfb.toFixed(2)}ms`);
              console.log(`[PERFORMANCE] Content download: ${downloadTime.toFixed(2)}ms`);
              console.log(`[PERFORMANCE] DOM Interactive: ${domInteractive.toFixed(2)}ms`);
              console.log(`[PERFORMANCE] DOM Content Loaded: ${domContentLoaded.toFixed(2)}ms`);
              console.log(`[PERFORMANCE] DOM Complete: ${domComplete.toFixed(2)}ms`);
              console.log(`[PERFORMANCE] Load Event: ${loadEvent.toFixed(2)}ms`);
            }
          } catch (error) {
            console.error('Error logging navigation timing metrics:', error);
          }
        }

        // Start measuring component render time
        try {
          startTimer(`${pageName}:render`);

          // Use requestAnimationFrame to measure when the page is visually complete
          if (typeof requestAnimationFrame !== 'undefined') {
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                try {
                  endTimer(`${pageName}:render`, 'Component rendered');

                  // Measure First Contentful Paint if available
                  if (typeof performance !== 'undefined') {
                    try {
                      const paintEntries = performance.getEntriesByType('paint');
                      const fcp = paintEntries.find(entry => entry.name === 'first-contentful-paint');

                      if (fcp) {
                        logPerformance(`${pageName} - First Contentful Paint`, fcp.startTime);
                      }
                    } catch (error) {
                      console.error('Error measuring First Contentful Paint:', error);
                    }
                  }
                } catch (error) {
                  console.error('Error ending render timer:', error);
                }
              });
            });
          }
        } catch (error) {
          console.error('Error starting render timer:', error);
        }
      }
    } catch (error) {
      console.error('Error in PerformanceMonitor:', error);
    }

    return () => {
      if (isDebug) {
        try {
          console.log(`[PERFORMANCE] Page ${pageName} unmounted`);
        } catch (error) {
          console.error('Error logging page unmount:', error);
        }
      }
    };
  }, [pageName, isDebug]);

  return <>{children}</>;
};

export default PerformanceMonitor;
